/**
 * Round and hand progression logic
 */
import { EnginePlayer, GameFlowStateType, GameState } from './types'

/**
 * Determine the next game flow state based on current game conditions
 *
 * This is the primary decision function that determines what the engine should
 * do next: continue betting, progress to next round, or complete the hand.
 * It evaluates game state in priority order to handle overlapping conditions correctly.
 *
 * Flow decision logic (evaluated in order):
 * 1. **complete_hand**: Hand is over, award pot (one player left, showdown reached, or all all-in)
 * 2. **complete_round**: Betting round done, deal community cards (all acted, bets equal)
 * 3. **all_players_all_in**: No more betting possible, run out board to showdown
 * 4. **continue_hand**: Continue normal betting action
 *
 * Priority ordering is critical:
 * - Check `isHandComplete` first (most terminal state)
 * - Then `isRoundComplete` (triggers card dealing)
 * - Then `areAllPlayerAllIn` (skips betting, deals all remaining cards)
 * - Default to `continue_hand` (normal betting continues)
 *
 * Called by:
 * - Engine after every player action
 * - Engine after progressing to new betting round
 * - Used to determine control flow in game loop
 *
 * Poker rules enforced through helper functions:
 * - `isHandComplete`: One player remains, reached showdown, or all all-in
 * - `isRoundComplete`: All active players acted and bets equal
 * - `areAllPlayerAllIn`: All remaining players are all-in (no action possible)
 *
 * @param state - Current game state
 * @returns Flow state indicating what should happen next
 *
 * @example
 * // After Player 3 folds (only Player 1 remains)
 * getGameStateFlow(state) // => 'complete_hand'
 *
 * @example
 * // Flop: P1 bets $100, P2 calls (both acted, bets equal)
 * getGameStateFlow(state) // => 'complete_round' (deal turn card)
 *
 * @example
 * // Turn: P1 all-in $500, P2 all-in $500 (both all-in)
 * getGameStateFlow(state) // => 'all_players_all_in' (run out river, skip betting)
 *
 * @example
 * // Flop: P1 bets $100, P2 yet to act
 * getGameStateFlow(state) // => 'continue_hand' (wait for P2 action)
 */
export function getGameStateFlow(state: GameState): GameFlowStateType {
  if (isHandComplete(state)) {
    return 'complete_hand'
  }
  if (isRoundComplete(state) && !areAllPlayerAllIn(state)) {
    return 'complete_round'
  }
  if (areAllPlayerAllIn(state)) {
    return 'all_players_all_in'
  }
  return 'continue_hand'
}

/**
 * Predicate: Player is in 'active' state (can take actions)
 *
 * Active players can check, bet, call, raise, or fold. They have chips remaining
 * and are currently participating in betting action.
 *
 * @param player - Player to check
 * @returns True if player state is 'active'
 */
const isActive = (player: EnginePlayer): boolean => player.state === 'active'

/**
 * Check if all remaining players are all-in (no more betting action possible)
 *
 * When all players still in the hand are all-in, no more betting can occur.
 * The engine should skip directly to showdown by dealing all remaining
 * community cards.
 *
 * Poker rule: A hand with all all-in players proceeds immediately to showdown.
 * No further player actions are possible or required.
 *
 * Called by: getGameStateFlow() to determine if betting should continue
 *
 * Edge cases handled:
 * - No active players (defensive check - shouldn't happen in valid game state)
 * - Only one player left after others fold (not considered "all all-in")
 *
 * @param state - Current game state
 * @returns True if all remaining players are all-in
 *
 * @example
 * // Player A has $100, bets $100 (all-in)
 * // Player B has $150, calls $100 (still has $50)
 * // Returns false - Player B not all-in
 *
 * // Player A bets $100 (all-in), Player B calls $100 (all-in)
 * // Returns true - skip to showdown
 */
export function areAllPlayerAllIn(state: GameState): boolean {
  const activePlayers = state.players.filter(isActive)

  const nonAllIn = activePlayers.filter((p) => p.state !== 'allin')
  const allActed = nonAllIn.every((p) => p.hasActedThisRound)

  if (nonAllIn.length > 1) {
    return false
  }

  if (!allActed) {
    return false
  }
  const exemptPlayer = nonAllIn[0]
  if (exemptPlayer && exemptPlayer.currentBet < Math.max(...activePlayers.map((p) => p.currentBet))) {
    return false
  }
  return activePlayers.every((p) => p.id === exemptPlayer.id || p.state === 'allin')
}

/**
 * Check if the current betting round is complete
 * A round is complete when:
 * 1. All active players have equal bets (or are all-in/folded)
 * 2. All active players have acted (hasActedThisRound = true)
 * @param state - Current game state
 * @returns True if round is complete
 */
export function isRoundComplete(state: GameState): boolean {
  const activePlayers = state.players.filter((p) => p.state === 'active' || p.state === 'allin')

  // No active players (shouldn't happen, but handle gracefully)
  if (activePlayers.length === 0) {
    return true
  }

  // Only one player left (others folded)
  // Exclude 'waiting' and 'sittingout' players who are not in the current hand
  const nonFoldedPlayers = state.players.filter(
    (p) =>
      p.state !== 'folded' &&
      p.state !== 'out' &&
      p.state !== 'waiting' &&
      p.state !== 'sittingout' &&
      p.state !== 'awaiting_rebuy'
  )
  if (nonFoldedPlayers.length === 1) {
    return true
  }

  // Check if all active (non-allin) players have acted
  const activeNonAllIn = activePlayers.filter(isActive)

  // Check if all bets are equal
  const activeBets = activeNonAllIn.map((p) => p.currentBet)
  const allInBets = activePlayers.filter((p) => p.state === 'allin').map((p) => p.currentBet)
  const allBets = [...activeBets, ...allInBets]

  if (allBets.length === 0) {
    return true
  }

  // All active players must have equal bets
  const maxBet = Math.max(...allBets)
  const allEqual = activeBets.every((bet) => bet === maxBet)

  if (allEqual === false) {
    return false
  }

  const allActed = activeNonAllIn.every((p) => p.hasActedThisRound)

  return allActed
}

/**
 * Check if the hand is complete (should go to showdown or award pot)
 * A hand is complete when:
 * 1. Only one player remains (others folded)
 * 2. Board state is showdown
 * 3. All players are all-in
 * @param state - Current game state
 * @returns True if hand is complete
 */
export function isHandComplete(state: GameState): boolean {
  // Exclude 'waiting' and 'sittingout' players who are not in the current hand
  const nonFoldedPlayers = state.players.filter(
    (p) =>
      p.state !== 'folded' &&
      p.state !== 'out' &&
      p.state !== 'waiting' &&
      p.state !== 'sittingout' &&
      p.state !== 'awaiting_rebuy'
  )

  // Only one player remains
  if (nonFoldedPlayers.length === 1) {
    return true
  }

  // Reached showdown
  if (state.boardState === 'showdown') {
    return true
  }

  // All remaining players are all-in (skip to showdown)
  const allAllin = nonFoldedPlayers.every((p) => p.state === 'allin')
  if (allAllin) {
    return false
  }

  return false
}

/**
 * Get the next active player (CLOCKWISE from current seat)
 * Works with ACTIVE PLAYERS ONLY - skips empty seats and folded players
 * Clockwise = decreasing seat indices with wraparound
 * @param state - Current game state
 * @param currentSeat - Current seat index
 * @returns Next player's ID or null if no active players
 */
export function getNextActivePlayer(state: GameState, currentSeat: number): string | null {
  // Filter and sort active players by seat (clockwise order)
  const activePlayers = state.players.filter((p) => p.state === 'active').sort((a, b) => a.seatId - b.seatId)

  if (activePlayers.length === 0) {
    return null
  }

  // Find the maximum seat ID to determine the total seat range
  // This handles sparse seating (e.g., only seats 0 and 9 occupied)
  const maxSeatId = Math.max(...state.players.map((p) => p.seatId), currentSeat)
  const totalSeats = maxSeatId + 1

  // Find current player's index in active players list
  const currentIndex = activePlayers.findIndex((p) => p.seatId === currentSeat)

  if (currentIndex === -1) {
    // Current seat not in active players, find next active player clockwise
    // Start from currentSeat and go clockwise (decreasing) until we find an active player
    for (let offset = 1; offset <= totalSeats; offset++) {
      const testSeat = (currentSeat - offset + totalSeats) % totalSeats
      const activePlayer = activePlayers.find((p) => p.seatId === testSeat)
      if (activePlayer) {
        return activePlayer.id
      }
    }
    return null
  }

  // Move to next active player (clockwise)
  // Find the next player in clockwise order (decreasing seat IDs with wrap)
  const currentSeatId = activePlayers[currentIndex].seatId

  // Look for next active player clockwise
  for (let offset = 1; offset <= totalSeats; offset++) {
    const nextSeat = (currentSeatId - offset + totalSeats) % totalSeats
    const nextPlayer = activePlayers.find((p) => p.seatId === nextSeat)
    if (nextPlayer) {
      return nextPlayer.id
    }
  }

  return null
}

/**
 * Get the first player to act in a betting round
 * Works with ACTIVE PLAYERS ONLY
 * Preflop: Player after big blind (clockwise)
 * Postflop: Player after button (clockwise)
 * @param state - Current game state
 * @returns Player ID or null
 */
export function getFirstPlayerToAct(state: GameState): string | null {
  const activePlayers = state.players.filter((p) => p.state === 'active')

  if (activePlayers.length === 0) {
    return null
  }

  // Preflop: First to act is player after big blind (clockwise)
  if (state.boardState === 'deal') {
    // Find the BB player by their position field (assigned correctly by startHand)
    // This is more reliable than arithmetic which doesn't account for inactive players
    const bbPlayer = state.players.find((p) => p.position === 'bb')

    if (bbPlayer) {
      // Get next active player clockwise from BB
      return getNextActivePlayer(state, bbPlayer.seatId)
    }

    // Fallback: Calculate BB seat if position not set (e.g., in tests or
    // malformed states). Uses `findNextActiveSeat` which matches the default
    // `blindPolicy: 'skip'` used by cash games. Callers that ran
    // `startHand` with `blindPolicy: 'force'` (tournaments) and rely on this
    // fallback would compute a different BB seat than `startHand` did — but
    // in practice tournament code paths always have `position === 'bb'`
    // stamped by `startHand`, so the primary path above hits. Test fixtures
    // exercising tournament rules should set `position` directly.
    const activeCount = activePlayers.length
    const isHeadsUp = activeCount === 2

    // Calculate blind seats the same way startHand does for `'skip'` policy
    const smallBlindSeat = isHeadsUp ? state.button : findNextActiveSeat(state, state.button)

    const bigBlindSeat = isHeadsUp ? findNextActiveSeat(state, state.button) : findNextActiveSeat(state, smallBlindSeat)

    // Get next active player clockwise from BB
    return getNextActivePlayer(state, bigBlindSeat)
  }

  // Postflop: First to act is player after button (clockwise)
  return getNextActivePlayer(state, state.button)
}

/**
 * Find the next active seat clockwise from a given seat
 * Works with ACTIVE PLAYERS ONLY - skips players with no chips or in 'out' state
 * Clockwise = decreasing seat indices with wraparound
 *
 * This is useful for button rotation and blind position calculation where you need
 * seat numbers rather than player IDs.
 *
 * @param state - Current game state
 * @param fromSeat - Starting seat index
 * @returns Next active seat number (or fromSeat if no active players found)
 */
/** Clockwise search from `fromSeat` for the next seat whose player matches `eligible`. */
function findNextSeatMatching(
  state: GameState,
  fromSeat: number,
  eligible: (p: EnginePlayer) => boolean
): number {
  const matches = state.players.filter(eligible)
  if (matches.length === 0) return fromSeat

  const maxSeatId = Math.max(...state.players.map((p) => p.seatId), fromSeat)
  const totalSeats = maxSeatId + 1
  for (let offset = 1; offset <= totalSeats; offset++) {
    const testSeat = (fromSeat - offset + totalSeats) % totalSeats
    if (matches.some((p) => p.seatId === testSeat)) return testSeat
  }
  return fromSeat
}

/** Players who are dealt cards and act this hand. */
const isActiveForHand = (p: EnginePlayer) =>
  p.chips > 0 && p.state !== 'out' && p.state !== 'sittingout' && p.state !== 'awaiting_rebuy'

/**
 * Players eligible to POST A BLIND. Includes `sittingout` — standard poker
 * rules require them to pay when the button passes; they still don't get
 * dealt cards or act.
 */
const isBlindEligible = (p: EnginePlayer) =>
  p.chips > 0 && p.state !== 'out' && p.state !== 'awaiting_rebuy'

export function findNextActiveSeat(state: GameState, fromSeat: number): number {
  return findNextSeatMatching(state, fromSeat, isActiveForHand)
}

export function findNextBlindSeat(state: GameState, fromSeat: number): number {
  return findNextSeatMatching(state, fromSeat, isBlindEligible)
}

/**
 * Reset the hasActedThisRound flag for other players with matching state
 *
 * This function is used when a player's action (like a raise) invalidates
 * the actions of other players, requiring them to act again in the current
 * betting round.
 *
 * @param players - Array of all players in the game
 * @param playerId - ID of the player who just acted (excluded from reset)
 * @param state - Player state to match when resetting (defaults to 'active')
 * @returns New array with hasActedThisRound reset for matching players
 *
 * @example
 * // After Player A raises, all other active players need to act again
 * const updatedPlayers = setNotActedThisRoundOtherPlayers(state.players, playerA.id, 'active')
 */
export function setNotActedThisRoundOtherPlayers(
  players: EnginePlayer[],
  currentPlayer: EnginePlayer,
  state: EnginePlayer['state'] = 'active'
) {
  // Map through all players and reset hasActedThisRound to false for:
  // - Players who are NOT the current player (p.id !== playerId)
  // - AND have the specified state (defaults to 'active')
  // - for player than don't match currentBet
  // All other players are returned unchanged
  return players.map((p) =>
    p.id !== currentPlayer.id && p.state === state && p.currentBet <= currentPlayer.currentBet
      ? { ...p, hasActedThisRound: false }
      : p
  )
}
